import SendIcon from '@mui/icons-material/Send';
import { useEventCallback } from '@mui/material/utils';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { EmailTemplatesPopover, useDialogs } from '@work4all/components';
import { CustomToolbarAction } from '@work4all/components/lib/dataDisplay/actions-bar/ToolbarTypes';
import { useEmailTemplates } from '@work4all/components/lib/hooks';

import { Appointment } from '@work4all/models/lib/Classes/Appointment.entity';
import { EMailTemplate } from '@work4all/models/lib/Classes/EMailTemplate.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useDeepMemo } from '@work4all/utils/lib/hooks/use-deep-memo';

import { EmailTemplateButtonContextProps } from '../../../../containers/mask-overlays/components/email-template-button/EmailTemplateButtonProvider';
import { EmailOverlayController } from '../../../../containers/mask-overlays/mask-overlay/views/email/EmailOverlayController';
import { settings, useSetting } from '../../../../settings';

interface UseEmailTemplateProps extends EmailTemplateButtonContextProps {
  appointments: Appointment[];
}

export const useAppointmentEmailTemplate = (props: UseEmailTemplateProps) => {
  const { noTemplate, eMailTemplateKind, getEmailParams } = props;

  const { t } = useTranslation();

  const appointment = useDeepMemo(
    () => props.appointments?.[0],
    [props.appointments?.[0]]
  );

  const emailTemplatesRef = useRef<HTMLButtonElement>(null);
  const templates = useEmailTemplates(eMailTemplateKind);

  const { value: standardTemplates } = useSetting(
    settings.standardEmailTemplates()
  );

  const dialogs = useDialogs();

  const openEmailMask = useCallback(
    async (emaiTemplate: EMailTemplate) => {
      const template = !emaiTemplate?.id ? null : emaiTemplate;
      const { entityTemplate, params } = await getEmailParams(
        template,
        appointment
      );
      const processedMailTemplateArgs = JSON.parse(
        params.processedMailTemplateArgs
      );

      const emailProps = {
        entity: Entities.eMail,
        id: null,
        template: entityTemplate,
        params: {
          basedon: 'EmailTemplate',
          emailTemplate: template ? JSON.stringify(template) : undefined,
          processedMailTemplateArgs: JSON.stringify({
            ...processedMailTemplateArgs,
            noDirectContact: !appointment.contact,
          }),
          forceNoTemplate:
            emaiTemplate?.id?.toString() === '0' &&
            !standardTemplates[emaiTemplate.eMailTemplateKind]
              ? 'true'
              : 'false',
          ...params,
        },
      };

      const dialog = dialogs.overlay(
        <EmailOverlayController {...emailProps} onAfterSave={closeDialog} />
      );
      function closeDialog() {
        dialogs.close(dialog.id);
      }
    },
    [getEmailParams, appointment, standardTemplates, dialogs]
  );

  const openEmailTemplatesPopover = useEventCallback(() => {
    const { id } = dialogs.open(EmailTemplatesPopover, {
      noTemplate,
      eMailTemplateKind,
      anchorEl: emailTemplatesRef.current,
      onClose: closeDialog,
      onTemplateClick: (template) => {
        closeDialog();
        openEmailMask(template);
      },
    });

    function closeDialog() {
      dialogs.close(id);
    }
  });

  const action: CustomToolbarAction = useMemo(() => {
    return {
      rootRef: emailTemplatesRef,
      actionKey: 'EMAIL_FROM_APPOINTMENT',
      mode: 'Edit',
      title: t('TICKET.SEND_EMAIL'),
      IconComponent: SendIcon,
      handler: (_id, event) => {
        emailTemplatesRef.current = event.target as HTMLButtonElement;
        const standardTemplateId = standardTemplates[eMailTemplateKind];
        if (standardTemplateId) {
          const standardTemplate = templates?.find(
            (t) => t.id === standardTemplateId
          );
          openEmailMask(standardTemplate);
        } else {
          openEmailTemplatesPopover();
        }
      },
      sections: ['mobile', 'preview'],
    };
  }, [
    eMailTemplateKind,
    openEmailMask,
    openEmailTemplatesPopover,
    standardTemplates,
    t,
    templates,
  ]);

  return useMemo(() => {
    return action;
  }, [action]);
};
